import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { auth } from "helpers/Firebase";
import { setCurrentUser } from "helpers/Utils";
import { getUserByUid, getRoleByName } from "helpers/mongo";
import { adminRoot } from "constants/defaultValues";

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset
} from "firebase/auth";

// 🔹 Login Thunk
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ user, navigate }, thunkAPI) => {
    try {
      const { email, password } = user;
      const loginResponse = await signInWithEmailAndPassword(auth, email, password);

      if (loginResponse.user) {
        const user1 = await getUserByUid();
        let item = { role: 2, ...user1 };
        // Fetch user permissions in parallel for efficiency
        const [perms] = await Promise.all([getRoleByName(item.role)]);

        item.permissions = perms?.data[0]?.permissions || [];
        setCurrentUser(item);
        navigate(adminRoot);
        return item;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔹 Register Thunk
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async ({ user, navigate }, thunkAPI) => {
    try {
      const { email, password } = user;
      const registerResponse = await createUserWithEmailAndPassword(auth, email, password);

      if (registerResponse.user) {
        const item = { uid: registerResponse.user.uid };
        setCurrentUser(item);
        navigate(adminRoot);
        return item;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔹 Logout Thunk
export const logoutUser = createAsyncThunk("auth/logoutUser", async (navigate) => {
  await signOut(auth);
  setCurrentUser(null);
  navigate(adminRoot);
});

// 🔹 Forgot Password Thunk
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, thunkAPI) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return email;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔹 Reset Password Thunk
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ resetPasswordCode, newPassword, navigate }, thunkAPI) => {
    try {
      await confirmPasswordReset(auth, resetPasswordCode, newPassword);
      navigate("/login");
      return { resetPasswordCode, newPassword };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔹 Auth Slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    currentUser: null,
    forgotUserMail: "",
    newPassword: "",
    resetPasswordCode: "",
    loading: false,
    error: null,
  },
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotUserMail = action.payload;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordCode = action.payload.resetPasswordCode;
        state.newPassword = action.payload.newPassword;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.currentUser = null;
        state.forgotUserMail = "";
        state.newPassword = "";
        state.resetPasswordCode = "";
        state.error = null;
      });
  },
});

export const { clearAuthError } = authSlice.actions;
export default authSlice.reducer;
